"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
exports.useMap = exports.MapProvider = void 0;
var _auth = require("@ori/auth");
var _environment = require("@ori/environment");
var _globalApiClientReact = require("@ori/global-api-client-react");
var _i18n = require("@ori/i18n");
var _react = require("react");
var _SearchConfigurationProvider = require("../containers/SearchConfigurationProvider");
var _useGeocodingSearch = require("../hooks/useGeocodingSearch");
var _utils = require("../utils");
var _jsxRuntime = require("react/jsx-runtime");
const useMapProvider = (param)=>{
    let { deliveryChannelId, onDeliveryItemActivation, togglePickupPointsDialog } = param;
    const { formattedToken } = (0, _auth.useToken)();
    const { tenant } = (0, _environment.useEnvironment)();
    const { language } = (0, _i18n.useLanguage)();
    const headers = (0, _react.useMemo)(()=>(0, _utils.getHeaders)({
            currentLanguage: language,
            tenant
        }), [
        language,
        tenant
    ]);
    const { credentials, mapProvider, zoomAfterSelection, fallbackViewSettings, viewSettings, setViewSettings } = (0, _SearchConfigurationProvider.useSearchConfiguration)();
    const [isMapInfoOpen, setIsMapInfoOpen] = (0, _react.useState)(false);
    const [selectedDelivery, setSelectedDelivery] = (0, _react.useState)(deliveryChannelId);
    const [selectedLocation, setSelectedLocation] = (0, _react.useState)();
    // Location of selected delivery channel
    const apiClient = (0, _globalApiClientReact.useApiClient)();
    // Current location from browser geolocation API
    const [currentLocation, setCurrentLocation] = (0, _react.useState)();
    const isCurrentLocationAllowed = currentLocation !== null;
    // Handle initial location picking
    (0, _react.useEffect)(()=>{
        if (viewSettings) {
            return;
        }
        const getInitialLocation = async ()=>{
            let locationSet = false;
            const updateMap = (location)=>{
                if (location && !locationSet) {
                    setViewSettings({
                        location,
                        zoom: zoomAfterSelection
                    });
                    locationSet = true;
                }
            };
            // Use browser geolocation API to get current location
            if (window.isSecureContext) {
                navigator.geolocation.getCurrentPosition((position)=>{
                    const { latitude, longitude } = position.coords;
                    setCurrentLocation({
                        latitude,
                        longitude
                    });
                    updateMap({
                        latitude,
                        longitude
                    });
                }, ()=>setCurrentLocation(null));
            }
            // If deliveryChannelId is provided, fetch its coordinates
            if (deliveryChannelId !== undefined) {
                const response = await (0, _globalApiClientReact.getDeliveryChannelCoordinates)(apiClient, deliveryChannelId, headers).catch(()=>undefined);
                if (response) {
                    setSelectedLocation(response.data);
                    updateMap(response.data);
                    return;
                }
            }
            // Try to locate the primary address
            const primaryAddress = await (0, _utils.getUrl)({
                basePath: '/system/ajax/DeliverySelection/GetPrimaryAddress',
                host: window.location.origin,
                currentLanguage: language,
                formattedToken,
                tenant
            });
            if (primaryAddress === null || primaryAddress === void 0 ? void 0 : primaryAddress.city) {
                const response = await (0, _useGeocodingSearch.getGeocodingSearchResult)({
                    provider: mapProvider,
                    credentials,
                    searchTerm: primaryAddress.city,
                    currentLanguage: language,
                    formattedToken,
                    tenant
                }).catch(()=>undefined);
                if (response) {
                    updateMap(response);
                    return;
                }
            }
            if (primaryAddress === null || primaryAddress === void 0 ? void 0 : primaryAddress.address) {
                const response = await (0, _useGeocodingSearch.getGeocodingSearchResult)({
                    provider: mapProvider,
                    credentials,
                    searchTerm: primaryAddress.address,
                    currentLanguage: language,
                    formattedToken,
                    tenant
                }).catch(()=>undefined);
                if (response) {
                    updateMap(response);
                    return;
                }
            }
            // Fallback to default view settings from the configuration
            setViewSettings(fallbackViewSettings);
        };
        void getInitialLocation();
    }, [
        apiClient,
        credentials,
        deliveryChannelId,
        fallbackViewSettings,
        viewSettings,
        formattedToken,
        headers,
        language,
        mapProvider,
        setViewSettings,
        tenant,
        zoomAfterSelection
    ]);
    const setSelectedDeliveryCb = (0, _react.useCallback)(function(id, location) {
        let zoom = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : zoomAfterSelection;
        setSelectedDelivery(id);
        setSelectedLocation(location);
        if (location) {
            // Do not zoom out if current zoom is bigger
            setViewSettings({
                location,
                zoom: Math.max(zoom, zoomAfterSelection)
            });
        }
    }, [
        setViewSettings,
        zoomAfterSelection
    ]);
    return (0, _react.useMemo)(()=>({
            currentLocation: currentLocation !== null && currentLocation !== void 0 ? currentLocation : undefined,
            selectedLocation,
            selectedDelivery,
            setSelectedDelivery: setSelectedDeliveryCb,
            isCurrentLocationAllowed,
            togglePickupPointsDialog,
            onDeliveryItemActivation,
            isMapInfoOpen,
            setIsMapInfoOpen
        }), [
        currentLocation,
        selectedLocation,
        selectedDelivery,
        setSelectedDeliveryCb,
        isCurrentLocationAllowed,
        togglePickupPointsDialog,
        onDeliveryItemActivation,
        isMapInfoOpen
    ]);
};
const mapContext = /*#__PURE__*/ (0, _react.createContext)(null);
const MapProvider = (param)=>{
    let { children, ...props } = param;
    return /*#__PURE__*/ (0, _jsxRuntime.jsx)(mapContext.Provider, {
        value: useMapProvider(props),
        children: children
    });
};
exports.MapProvider = MapProvider;
const useMap = ()=>{
    const ctx = (0, _react.useContext)(mapContext);
    if (!ctx) {
        throw new Error('useMap must be used within a MapProvider');
    }
    return ctx;
};
exports.useMap = useMap;
